import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Home as HomeIcon, Search, Trophy, LayoutDashboard, LogOut, Menu, X } from "lucide-react";
import { BrandLogo } from "./BrandLogo";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { dashPathFor } from "./GoogleAuth";
import { useMobile } from "../hooks/useMobile";
import { MobileHeader } from "./MobileHeader";
import NotificationBell from "./NotificationBell";

const navLink = ({ isActive }) =>
  `flex items-center gap-1.5 text-sm font-semibold transition-colors duration-150 ${isActive ? "text-[#171B33]" : "text-slate-500 hover:text-[#171B33]"}`;

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const mobile = useMobile();

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const dashPath = dashPathFor(user);

  return (
    <header className="site-header sticky top-0 z-50 bg-white border-b-2 border-[#171B33]">
      {mobile ? <MobileHeader settings={settings} /> : <>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
          <BrandLogo settings={settings} testid="nav-brand" />
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          <NavLink to="/" data-testid="nav-home-link" className={navLink} end><HomeIcon className="w-4 h-4" /> Home</NavLink>
          <NavLink to="/institutes" data-testid="nav-institutes-link" className={navLink}><Search className="w-4 h-4" /> Find Institutes</NavLink>
          <NavLink to="/referrals" data-testid="nav-referral-link" className={navLink}>Referral</NavLink>
          <NavLink to="/rankings" data-testid="nav-rankings-link" className={navLink}><Trophy className="w-4 h-4" /> Rankings</NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-7">
          {user ? (
            <>
              <NotificationBell />
              <Link to={dashPath} data-testid="nav-dashboard-link" className="flex items-center gap-1.5 text-sm font-semibold hover:text-lime-600 transition-colors duration-150">
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <button data-testid="nav-logout-btn" onClick={() => { logout(); navigate("/"); }}
                className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-red-600 transition-colors duration-150">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link" className="text-sm font-semibold hover:text-lime-600 transition-colors duration-150">Login</Link>
              <Link to="/register" data-testid="nav-register-link" className="nb-btn nb-btn-lime nb-btn-sm">Sign up</Link>
            </>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-3">
          {user && <NotificationBell idSuffix="-mobile" />}
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t-2 border-[#171B33] bg-white px-4 py-4 space-y-3">
          <Link to="/" onClick={() => setOpen(false)} className="block text-sm font-semibold">Home</Link>
          <Link to="/institutes" onClick={() => setOpen(false)} className="block text-sm font-semibold">Find Institutes</Link>
          <Link to="/teachers" data-testid="nav-mobile-teachers" onClick={() => setOpen(false)} className="block text-sm font-semibold">Teachers</Link>
          <Link to="/home-tuition" data-testid="nav-mobile-tuition" onClick={() => setOpen(false)} className="block text-sm font-semibold">Home Tuition</Link>
          <Link to="/rankings" data-testid="nav-mobile-rankings" onClick={() => setOpen(false)} className="block text-sm font-semibold">Rankings</Link>
          {user ? (
            <>
              <Link to={dashPath} onClick={() => setOpen(false)} className="block text-sm font-semibold">Dashboard</Link>
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="block text-sm font-semibold text-red-600">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="block text-sm font-semibold">Login</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="block text-sm font-semibold text-lime-600">Sign up</Link>
            </>
          )}
        </div>
      )}
      </>}
    </header>
  );
};

export default Navbar;
